import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { MotionConfig } from "framer-motion";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { STAFF, ADMIN } from "@/lib/nav";
import { RequireAuth, RequireRole } from "@/components/app/Guards";
import AppShell from "@/components/app/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";
import Dashboard from "@/pages/Dashboard";
import JobsList from "@/pages/JobsList";
import JobWizard from "@/pages/JobWizard";
import JobDetail from "@/pages/job/JobDetail";
import { ModulePlaceholder, NotFound } from "@/pages/Placeholders";
import { PublicPlaceholder } from "@/pages/PublicPages";
import { PrivacyNotice } from "@/pages/public/PrivacyNotice";
import PitchDeckPage from "@/pages/PitchDeckPage";
import ArchitecturePage from "@/pages/ArchitecturePage";
import { CareersPage } from "@/pages/careers/CareersPage";
import CareersIndex from "@/pages/careers/CareersIndex";
import { ConfirmHost } from "@/components/app/Confirm";
import { LinksHost } from "@/pages/assessments/AssessmentLinksDialog";
import CandidatesList from "@/pages/candidates/CandidatesList";
import CandidateProfile from "@/pages/candidates/CandidateProfile";
import ImportWizard from "@/pages/candidates/import/ImportWizard";
import ImportHistory from "@/pages/candidates/import/ImportHistory";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsLayout from "@/pages/settings/SettingsLayout";
import BrandingSettings from "@/pages/settings/BrandingSettings";
import EmailSettings from "@/pages/settings/EmailSettings";
import OutboxSettings from "@/pages/settings/OutboxSettings";
import UsersSettings from "@/pages/settings/UsersSettings";
import AuditLogSettings from "@/pages/settings/AuditLogSettings";
import AssessPage from "@/pages/assess/AssessPage";
import WorkSamplePage from "@/pages/workSample/WorkSamplePage";
import TaskLibrary from "@/pages/settings/TaskLibrary";
import AssessmentsPage from "@/pages/assessments/AssessmentsPage";
import BuilderPage from "@/pages/assessments/builder/BuilderPage";
import InterviewPage from "@/pages/interview/InterviewPage";
import InterviewsPage from "@/pages/interviews/InterviewsPage";
import InterviewDetailPage from "@/pages/interviews/InterviewDetailPage";
import InterviewTemplate from "@/pages/settings/InterviewTemplate";
const ComparePage = lazy(() => import("@/pages/compare/ComparePage"));
import HrmsSettings from "@/pages/settings/HrmsSettings";
import EmailTemplates from "@/pages/settings/EmailTemplates";
import FitDefaults from "@/pages/settings/FitDefaults";
import AssessmentTemplates from "@/pages/settings/AssessmentTemplates";
import DataRequests from "@/pages/settings/DataRequests";
import SystemPage from "@/pages/settings/SystemPage";
import ConnectionsSettings from "@/pages/settings/connections/ConnectionsSettings";
const ReportsPage = lazy(() => import("@/pages/reports/ReportsPage"));
import PrivacyPortal from "@/pages/public/PrivacyPortal";
import PrivacySettings from "@/pages/settings/PrivacySettings";
import DpdpChecklist from "@/pages/settings/DpdpChecklist";
import Incidents from "@/pages/settings/Incidents";
import StoragePurge from "@/pages/settings/StoragePurge";

const transient = (e) => !e?.response || e.response.status >= 500 || e.response.status === 429;
const queryClient = new QueryClient({
  defaultOptions: { queries: {
    retry: (n, e) => transient(e) && n < 4,
    retryDelay: (n) => Math.min(8000, 600 * 2 ** n),
    refetchOnWindowFocus: false, staleTime: 10000,
  } },
});

const JobRedirect = () => <Navigate to={`/app/jobs/${useParams().id}/workspace`} replace />;
const CandidateRedirect = () => <Navigate to={`/app/candidates/${useParams().id}/resume`} replace />;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyNotice />} />
      <Route path="/pitch-deck" element={<PitchDeckPage />} />
      <Route path="/architecture" element={<ArchitecturePage />} />
      <Route path="/careers" element={<CareersIndex />} />
      <Route path="/careers/:slug" element={<CareersPage />} />
      <Route path="/assess/:token" element={<AssessPage />} />
      <Route path="/work-sample/:token" element={<WorkSamplePage />} />
      <Route path="/my-data/:token" element={<PrivacyPortal />} />
      <Route path="/interview/:token" element={<InterviewPage />} />
      <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="jobs" element={<JobsList />} />
        <Route path="jobs/new" element={<RequireRole roles={STAFF}><JobWizard /></RequireRole>} />
        <Route path="jobs/:id" element={<JobRedirect />} />
        <Route path="jobs/:id/:tab" element={<JobDetail />} />
        <Route path="candidates" element={<CandidatesList />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="candidates/import" element={<RequireRole roles={STAFF}><ImportWizard /></RequireRole>} />
        <Route path="candidates/import/:id" element={<RequireRole roles={STAFF}><ImportWizard /></RequireRole>} />
        <Route path="candidates/imports" element={<RequireRole roles={STAFF}><ImportHistory /></RequireRole>} />
        <Route path="candidates/:id" element={<CandidateRedirect />} />
        <Route path="candidates/:id/:tab" element={<CandidateProfile />} />
        <Route path="compare" element={<Suspense fallback={null}><ComparePage /></Suspense>} />
        <Route path="talent-pool" element={<RequireRole roles={STAFF}><ModulePlaceholder module="talent-pool" /></RequireRole>} />
        <Route path="assessments" element={<AssessmentsPage />} />
        <Route path="assessments/new" element={<RequireRole roles={STAFF}><BuilderPage /></RequireRole>} />
        <Route path="assessments/:id" element={<BuilderPage />} />
        <Route path="interviews" element={<InterviewsPage />} />
        <Route path="interviews/:id" element={<InterviewDetailPage />} />
        <Route path="reports" element={<Suspense fallback={null}><ReportsPage /></Suspense>} />
        <Route path="settings" element={<RequireRole roles={ADMIN}><SettingsLayout /></RequireRole>}>
          <Route index element={<Navigate to="branding" replace />} />
          <Route path="branding" element={<BrandingSettings />} />
          <Route path="email" element={<EmailSettings />} />
          <Route path="connections" element={<ConnectionsSettings />} />
          <Route path="outbox" element={<OutboxSettings />} />
          <Route path="users" element={<UsersSettings />} />
          <Route path="audit-log" element={<AuditLogSettings />} />
          <Route path="task-library" element={<TaskLibrary />} />
          <Route path="interview-template" element={<InterviewTemplate />} />
          <Route path="fit-scoring" element={<FitDefaults />} />
          <Route path="assessment-templates" element={<AssessmentTemplates />} />
          <Route path="data-requests" element={<DataRequests />} />
          <Route path="privacy" element={<PrivacySettings />} />
          <Route path="dpdp" element={<DpdpChecklist />} />
          <Route path="incidents" element={<Incidents />} />
          <Route path="storage-purge" element={<StoragePurge />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="email-templates" element={<EmailTemplates />} />
          <Route path="hrms" element={<HrmsSettings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user" transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}>
      <AuthProvider>
        <TooltipProvider delayDuration={200}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <ConfirmHost />
          <LinksHost />
          <Toaster theme="light" position="bottom-right" duration={3500} toastOptions={{ style: { fontFamily: "'Public Sans', sans-serif", borderRadius: 6, border: "1px solid #E3DED3", boxShadow: "0 4px 12px -4px rgba(27,30,26,0.10)" } }} />
        </TooltipProvider>
      </AuthProvider>
      </MotionConfig>
    </QueryClientProvider>
  );
}
